import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_BASE } from '../../config/env';
import { useQueue } from '../../context/QueueContext';
import { useAuth } from '../../context/AuthContext';
import { exportToExcel } from '../../utils/exportToExcel';

// Keep the initial dashboard response for this browser page session. React
// Strict Mode may remount this component in development; a remount must not
// request a second, potentially different dashboard snapshot.

let initialDashboardRequest = null;

function fetchCombinedDashboard(sdate, edate) {
  const isInitialLoad = !sdate && !edate;

  if (isInitialLoad && initialDashboardRequest) {
    return initialDashboardRequest;
  }

  const request = axios.get(`${API_BASE}/combinedDashbaord`, {
    params: {
      type: 'combined',
      sdate: sdate,
      edate: edate,
    },
  });

  if (isInitialLoad) {
    initialDashboardRequest = request;
    request.catch(() => {
      initialDashboardRequest = null;
    });
  }

  return request;
}

export default function CombinedDashboardModule({ activeFilter, sdate, edate}) {
  const { user } = useAuth();
  // console.log('user in combined dashboard module', user);
  const isDcrbVisible = Number(user?.usertype) === 5 || Number(user?.usertype) === 52;

  const [stationRows, setStationRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');

  // Re-fetch whenever a queued job finishes
  const { dashboardRefreshKey } = useQueue();

  useEffect(() => {
    // On a queue-triggered refresh, invalidate the cache so fresh data arrives
    if (dashboardRefreshKey > 0) {
      initialDashboardRequest = null;
    }
    loadStationData(sdate, edate);
  }, [sdate, edate, dashboardRefreshKey]);

  async function loadStationData(sdate, edate) {
    setLoading(true);
    setError('');
    // console.log("activeFilter in charcter  filter ", activeFilter);
    try {
      const res = await fetchCombinedDashboard(sdate, edate);
      if (res.data && res.data.DashboardResult && res.data.DashboardResult.length > 0) {
        // console.log(' res.data.DashboardResult.length ', res.data.DashboardResult.length);
        // console.log(' res.data.DashboardResult ',res.data.DashboardResult);
        setStationRows(res.data?.DashboardResult || []);
      }
      // setMinDate(sdate);
      // setMaxDate(edate);
    } catch (e) {
      console.warn('Using default station reference data:', e.message);
    } finally {
      setLoading(false);
    }
  }

  

  function fmt(dateStr) {
    if (!dateStr) return '';
    return dateStr.split('-').reverse().join('-');
  }

  // Highlight classes based on selected dropdown options (activeFilter)
  const getHighlightClass = (colName) => {
    if (!activeFilter) return '';
    const nameMap = {
      totalps: 'ps',
      totalliu: 'liu',
      totaldcrb: 'dcrb',
      totaldcp: 'dcp',
    };
    return nameMap[activeFilter] === colName ? 'highlight-column' : '';
  };

  function handleExcelExport() {
    const utype = Number(user?.usertype);
    const headers = [
      'Sr.No.',
      'Verification Type',
      'Received',
      'Pending',
      ...(utype === 5 || utype === 1 ? ['PS'] : []),
      ...(utype === 5 || utype === 52 ? ['DCRB'] : []),
      ...(utype === 5 || utype === 51 ? ['LIU'] : []),
      ...(utype === 5 ? ['DCP'] : []),
      ...(utype === 5 || utype === 1 ? ['Own to Other'] : []),
      ...(utype === 5 ? ['Other to Own'] : []),
      ...(utype === 51 ? ['Other to Own LIU'] : []),
      ...(utype === 52 ? ['Other to Own DCRB'] : []),
      ...(utype === 1 ? ['Other to Own PS'] : []),
    ];
    const data = stationRows.map((r, i) => [
      i + 1,
      r.verification_type,
      r.request_count,
      r.pending_count || 0,
      ...(utype === 5 || utype === 1 ? [r.pending_ps_count || 0] : []),
      ...(utype === 5 || utype === 52 ? [r.pending_dcrb_count || 0] : []),
      ...(utype === 5 || utype === 51 ? [r.pending_liu_count || 0] : []),
      ...(utype === 5 ? [r.pending_dcp_count || 0] : []),
      ...(utype === 5 || utype === 1 ? [r.own_to_other || 0] : []),
      ...(utype === 5 ? [r.other_to_own || 0] : []),
      ...(utype === 51 ? [r.other_to_ownLIU || 0] : []),
      ...(utype === 52 ? [r.other_to_ownDCRB || 0] : []),
      ...(utype === 1 ? [r.other_to_ownPS || 0] : []),
    ]);
    const fmtD = (d) => (d ? d.split('-').reverse().join('-') : '');
    const dateRange = (sdate || edate)
      ? `Date From: ${fmtD(sdate) || 'All'}    To: ${fmtD(edate) || 'All'}`
      : '';
    exportToExcel({
      title: 'Verification Dashboard',
      dateRange,
      headers,
      data,
      fileName: 'Verification_Dashboard',
      sheetName: 'Verification Dashboard',
    });
  }

  return (
    <div className="module-container card-upgrade" style={{ padding: '20px' }}>
        <div className="table-header-title">
        <h3>
            Verification Dashboard 
        </h3>
        <button
          className="btn-excel-export"
          onClick={handleExcelExport}
          title="Download Excel"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8.5 18l-2-3h1.2l1.3 2 1.3-2H11.5l-2 3 2.2 3H10.3l-1.3-2-1.3 2H6.3l2.2-3zm5 3v-6h1v5h2v1h-3z"/>
          </svg>
          Excel
        </button>
      </div>
      {/* Satyapan Station-Wise Verification Table */}
      <div className="table-wrapper image-styled-table-wrapper">
      
        <table className="image-styled-table">
          <thead>
            {/*<tr>
              <th colSpan="10" style={{ textAlign: 'center', fontSize: '18px', background: '#1e293b', color: '#fff', padding: '12px' }}>
                 Verification Dashboard 
              </th>
            </tr> */}
            {minDate || maxDate ? (
              <tr style={{ background: '#f8fafc' }}>
                <th colSpan="2" style={{ textAlign: 'center', color: '#1e293b', background: '#e2e8f0' }}>दिनांक से</th>
                <th colSpan="2" style={{ color: '#1e293b', background: '#f1f5f9' }}>{fmt(minDate)}</th>
                <th colSpan="2" style={{ textAlign: 'center', color: '#1e293b', background: '#e2e8f0' }}>दिनांक तक</th>
                <th colSpan={isDcrbVisible ? 4 : 3} style={{ color: '#1e293b', background: '#f1f5f9' }}>{fmt(maxDate)}</th>
              </tr>
            ) : null}
            <tr>
              <th>Sr.No.</th>
              <th>Verification Type</th>
              <th className={getHighlightClass('all')}>Received</th>
              <th className={getHighlightClass('remain')}>Pending</th>
              {(Number(user?.usertype) === 5 || Number(user?.usertype) === 1) && <th className={getHighlightClass('ps')}>PS</th>}
              {(Number(user?.usertype) === 5 || Number(user?.usertype) === 52) && <th className={getHighlightClass('dcrb')}>DCRB</th>}
              {(Number(user?.usertype) === 5 || Number(user?.usertype) === 51) && <th className={getHighlightClass('liu')}>LIU</th>}
              {(Number(user?.usertype) === 5) && <th className={getHighlightClass('dcp')}>DCP</th>}
              {(Number(user?.usertype) === 5 || Number(user?.usertype) === 1) && <th className={getHighlightClass('own_to_other')}>Own to Other</th>}
              {(Number(user?.usertype) === 5) && <th className={getHighlightClass('other_to_own')}>Other to Own</th>}
              {(Number(user?.usertype) === 51) && <th className={getHighlightClass('other_to_ownLIU')}>Other to Own LIU</th>}
              {(Number(user?.usertype) === 52) && <th className={getHighlightClass('other_to_ownDCRB')}>Other to Own DCRB</th>}
              {(Number(user?.usertype) === 1) && <th className={getHighlightClass('other_to_ownPS')}>Other to Own PS</th>}

            </tr>
          </thead>
          <tbody>
  {loading && (
    <tr>
      <td colSpan={isDcrbVisible ? 10 : 9} style={{ textAlign: 'center', padding: '20px' }}>
        लोड हो रहा है...
      </td>
    </tr>
  )}

  {error && (
    <tr>
      <td colSpan={isDcrbVisible ? 10 : 9} style={{ textAlign: 'center', color: 'red' }}>
        {error}
      </td>
    </tr>
  )}

  {!loading &&
    stationRows.map((r, i) => {
      const map = {
        Tenants: 'tenant',
        Employee: 'employee',
        Character: 'character',
        Domestic: 'domestic',
        Complaints: 'complaint',
        Postmortem: 'postmortem'
      };

      const linkType = map[r.verification_type];
      const isTotal = r.verification_type === 'TOTAL';

      // If TOTAL, show plain text; otherwise show Link
      const renderValue = (value, loc) => {
        const displayValue = value || 0;

        // NULL denotes a metric that is not applicable for this verification
        // type. Show zero, but do not link to a list endpoint that cannot
        // service that metric.
        if (isTotal || value == null) {
          return displayValue;
        }

        return (
          <Link
            to={`/characters?type=${linkType}&sdate=${sdate}&edate=${edate}&loc=${loc}`}
          >
            {displayValue}
          </Link>
        );
      };

      return (
        <tr key={i} className={i % 2 === 1 ? 'alt-row' : ''}>
          <td>{i + 1}</td>

          <td style={{ fontWeight: '600' }}>
            {r.verification_type}
          </td>

          <td className={getHighlightClass('all')}>
            {/* {renderValue(r.request_count , 'all')} */}
            {r.request_count}
          </td>

          <td className={getHighlightClass('remain')}>
            {renderValue(r.pending_count , 'remain')}
          </td>

          {(Number(user?.usertype) === 5 || Number(user?.usertype) === 1) && (
            <td className={getHighlightClass('ps')}>
              {renderValue(r.pending_ps_count , 'ps')}
            </td>
          )}

          {(Number(user?.usertype) === 5 || Number(user?.usertype) === 52) && (
            <td className={getHighlightClass('dcrb')}>
              {renderValue(r.pending_dcrb_count , 'dcrb')}
            </td>
          )}

         {(Number(user?.usertype) === 5 || Number(user?.usertype) === 51) && ( <td className={getHighlightClass('liu')}>
            {renderValue(r.pending_liu_count , 'liu')}
          </td>)}

         {(Number(user?.usertype) === 5) && ( <td className={getHighlightClass('dcp')}>
            {renderValue(r.pending_dcp_count , 'dcp')}
          </td>)}

        {(Number(user?.usertype) === 5 || Number(user?.usertype) === 1) &&  <td className={getHighlightClass('own_to_other')}>
            {renderValue(
              r.own_to_other,
              'own_to_other'
            )}
          </td>}

          {(Number(user?.usertype) === 5) &&  <td className={getHighlightClass('other_to_own')}>
            {renderValue(
              r.other_to_own,
              'other_to_own'
            )}
          </td>}

{ (Number(user?.usertype) === 51)&& <td className={getHighlightClass('other_to_ownLIU')}>
            {renderValue(
              r.other_to_ownLIU,
              'other_to_ownLIU'
            )}
          </td> }


{ (Number(user?.usertype) === 52)&& <td className={getHighlightClass('other_to_ownDCRB')}>
            {renderValue(
              r.other_to_ownDCRB,
              'other_to_ownDCRB'
            )}
          </td> }

{ (Number(user?.usertype) === 1)&& <td className={getHighlightClass('other_to_ownPS')}>
            {renderValue(
              r.other_to_ownPS,
              'other_to_ownPS'
            )}
          </td> }
        </tr>
      );
    })}
</tbody>
        </table>
      </div>
    </div>
  );
}
